import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';

import Frame from '../layout/Frame';
import { fetchPlanInfo } from '../requests/requests';
import { PlanInfo } from '../types/types';
import useErrorHandler from '../hooks/useErrorHandler';
import Panel from '../layout/Panel';
import PricingTable from '../components/Pricing/Table';
import { AppRoute, PlanType } from '../types/enums';

const Upgrade = () => {
  const [planInfo, setPlanInfo] = useState<PlanInfo>();
  const [loading, setLoading] = useState(true);
  const errorHandler = useErrorHandler();

  useEffect(() => {
    fetchPlanInfo()
      .then(({ data }) => {
        setPlanInfo(data);
        setLoading(false);
      })
      .catch(errorHandler);
  }, []);

  const isUnlimited = planInfo?.currentPlan === PlanType.PREMIUM_LARGE;

  return (
    <Frame>
      <Panel>
        <h1 style={{ marginBottom: '1rem' }}>Plans</h1>

        {planInfo && (
          <p>
            Your current plan includes {planInfo.limits.websites} websites and{' '}
            {planInfo.limits.dashboards} dashboards.
            <br />
            Upgrade your subscription and get more dashboards and websites.
          </p>
        )}

        <PricingTable loading={loading} currentPlan={planInfo?.currentPlan} />

        <p>
          <NavLink className="color-orange" to={AppRoute.CONTACT_US}>
            Contact us
          </NavLink>{' '}
          for discounts and a custom plan.
        </p>

        <h1 style={{ marginBottom: '1rem', marginTop: '2rem' }}>Usage</h1>
        {planInfo && (
          <>
            <p>
              So far, you have created {planInfo.usage.dashboards} (out of the{' '}
              {planInfo.limits.dashboards} available) dashboards and {planInfo.usage.websites} (out
              of the {planInfo.limits.websites} available) websites.
            </p>
            <div style={{ marginBottom: '3rem' }}>
              <h2 style={{ color: '#8d8d8d', display: 'flex', justifyContent: 'space-between' }}>
                <span>Dashboard usage</span>
                <span>
                  {planInfo.usage.dashboards} / {isUnlimited ? '∞' : planInfo.limits.dashboards}
                </span>
              </h2>
              <div style={{ position: 'relative' }}>
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    height: '0.5rem',
                    backgroundColor: '#8d8d8d30'
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    width:
                      planInfo.limits.dashboards && !isUnlimited
                        ? (100 * planInfo.usage.dashboards) / planInfo.limits.dashboards + '%'
                        : '100%',
                    maxWidth: '100%',
                    height: '0.5rem',
                    backgroundColor: isUnlimited
                      ? '#44eaae'
                      : getColor(planInfo.usage.dashboards / planInfo.limits.dashboards)
                  }}
                />
              </div>
            </div>
            <div style={{ marginBottom: '4rem' }}>
              <h2 style={{ color: '#8d8d8d', display: 'flex', justifyContent: 'space-between' }}>
                <span>Website usage</span>
                <span>
                  {planInfo.usage.websites} / {planInfo.limits.websites}
                </span>
              </h2>
              <div style={{ position: 'relative' }}>
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    height: '0.5rem',
                    backgroundColor: '#8d8d8d30'
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    width: planInfo.limits.websites
                      ? (100 * planInfo.usage.websites) / planInfo.limits.websites + '%'
                      : '100%',
                    maxWidth: '100%',
                    height: '0.5rem',
                    backgroundColor: getColor(planInfo.usage.websites / planInfo.limits.websites)
                  }}
                />
              </div>
            </div>
          </>
        )}
      </Panel>
    </Frame>
  );
};

const getColor = (ratio: number) => {
  if (ratio < 0.8) {
    return '#44eaae';
  }

  if (ratio < 1) {
    return '#fb7021';
  }

  return '#cb1111';
};

export default Upgrade;
